import { useState } from 'react'
import { fetcher, AdminClawMachinePrizesEndpoints, IUseClawMachinePrizes, AdminTokensEndpoints, IBlockchainTokenCatalogGetAllProps, AdminBlockchainTokenCatalog, AdminProductsEndpoints, IBlockchainNetworkGetAllProps, AdminBlockchainNetwork } from '@olagg/api-hooks'
import { AdminClawMachinePrize } from '@olagg/db-types'
import { IUseTokens } from '@olagg/api-hooks/interfaces/IUseTokens'
import { IUseProducts } from '@olagg/api-hooks/interfaces/IUseProducts'

interface PrizesResponse {
  clawMachinePrizes: AdminClawMachinePrize[]
  meta: {
    count: number
  }
}

const usePrizes = () => {
  const [loading, setLoading] = useState(false)

  const getTokens = async (filter: IUseTokens) => {
    try {
      setLoading(true)
      const endpoint = AdminTokensEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.tokens || []
    } catch (error) {
      console.error('Error fetching tokens:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getBlockchainTokens = async (filter: IBlockchainTokenCatalogGetAllProps) => {
    try {
      setLoading(true)
      const endpoint = AdminBlockchainTokenCatalog.all(filter)
      const response = await fetcher(endpoint)
      return response.blockchainTokens || []
    } catch (error) {
      console.error('Error fetching blockchain tokens:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getBlockchainNetworks = async (filter: IBlockchainNetworkGetAllProps) => {
    try {
      setLoading(true)
      const endpoint = AdminBlockchainNetwork.all(filter)
      const response = await fetcher(endpoint)
      return response.blockchainTokenNetworks || []
    } catch (error) {
      console.error('Error fetching blockchain networks:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getProducts = async (filter: IUseProducts) => {
    try {
      setLoading(true)
      const endpoint = AdminProductsEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.products || []
    } catch (error) {
      console.error('Error fetching products:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getPrizes = async (filter: IUseClawMachinePrizes): Promise<PrizesResponse> => {
    try {
      setLoading(true)
      const endpoint = AdminClawMachinePrizesEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching claw machine prizes:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getPrize = async (id: string): Promise<AdminClawMachinePrize> => {
    try {
      setLoading(true)
      const endpoint = AdminClawMachinePrizesEndpoints.find(id)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createPrize = async (data: Partial<AdminClawMachinePrize>): Promise<AdminClawMachinePrize> => {
    try {
      setLoading(true)
      const endpoint = AdminClawMachinePrizesEndpoints.create(data)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error creating season:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updatePrize = async (id: string, data: Partial<AdminClawMachinePrize>): Promise<AdminClawMachinePrize> => {
    try {
      setLoading(true)
      const endpoint = AdminClawMachinePrizesEndpoints.update(id, data)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error updating season:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deletePrize = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      const endpoint = AdminClawMachinePrizesEndpoints.delete(id)
      await fetcher(endpoint)
    } catch (error) {
      console.error('Error deleting claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    getPrizes,
    getPrize,
    createPrize,
    updatePrize,
    deletePrize,
    getTokens,
    getBlockchainTokens,
    getBlockchainNetworks,
    getProducts
  }
}

export default usePrizes
